import { useRef, useState } from "react"
import { useForm, FormProvider } from "react-hook-form"
import { ButtonPrimary, ButtonPrimarySoftLarge } from "~styled/button"
import PageContent from "~components/PageContent"
import FlexRow from "~components/FlexRow"
import { toast } from "react-toastify"

// Service
import FileUploadService from "~services/FileUploadService"
import styled from "styled-components"
import PageHeader from "~components/PageHeader"

const FileUploadPage = () => {
  const form = useForm()
  const [file, setFile] = useState(null)
  const [filePath, setFilePath] = useState("")
  const [isUploading, setIsUploading] = useState(false)
  const inputRef = useRef(null)

  const handleFileChange = e => {
    setFile(e.target.files[0])
    setFilePath("") // Clear file path when a new file is selected
  }

  const handleCopy = () => {
    if (filePath) {
      navigator.clipboard.writeText(filePath)
      toast.success("File path copied to clipboard!")
    }
  }

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file to upload")
      return
    }

    setIsUploading(true) // Lock buttons and show uploading status

    try {
      const response = await FileUploadService.postPublicFile(file)

      if (response) {
        setFilePath(response.file)
        toast.success("File uploaded successfully!")
        setFile(null) // Clear file selection after successful upload
      }
    } catch (error) {
      toast.error("Failed to upload file.")
    } finally {
      setIsUploading(false) // Unlock buttons after upload
    }
  }

  const handleButtonClick = () => {
    if (inputRef.current) {
      inputRef.current.click()
    }
  }

  return (
    <PageContent>
      <PageHeader
        title={"Public File Uploader"}
        subtitle={
          "Upload a file to be publicly accessible. These public files can be embedded in markdown for blog posts, for example. Please remember to copy the file path and save it somewhere secure, as you will not be able to access it again later."
        }
      />{" "}
      <FormProvider {...form}>
        <FlexRow style={{ justifyContent: "center", marginBottom: 20 }}>
          <StyledFileUploadContainer>
            <ButtonPrimarySoftLarge
              onClick={handleButtonClick}
              disabled={isUploading}
            >
              {isUploading ? "Uploading..." : "Choose a file"}
            </ButtonPrimarySoftLarge>
            <input
              ref={inputRef}
              type="file"
              style={{ display: "none" }}
              onChange={handleFileChange}
              disabled={isUploading}
            />
            {file && (
              <FileListItem>
                <span>{file.name}</span>
              </FileListItem>
            )}
          </StyledFileUploadContainer>
        </FlexRow>
        <FlexRow style={{ justifyContent: "center" }}>
          <ButtonPrimary onClick={handleUpload} disabled={isUploading}>
            {isUploading ? "Uploading..." : "Upload"}
          </ButtonPrimary>
        </FlexRow>
        {filePath && (
          <FlexRow style={{ justifyContent: "center", marginTop: 20 }}>
            <div style={{ marginRight: 10 }}>
              <strong>Uploaded File Path:</strong> {filePath}
            </div>
            <ButtonPrimary onClick={handleCopy} disabled={isUploading}>
              Copy File Path
            </ButtonPrimary>
          </FlexRow>
        )}
      </FormProvider>
    </PageContent>
  )
}

// -------------------- STYLED COMPONENTS --------------------

const StyledFileUploadContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
`

const FileListItem = styled.div`
  margin-top: 10px;
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  text-align: center;
`

export default FileUploadPage
